// Display masks for numbers a payer types or reads. The separators exist on
// screen only: callers keep and send the cleaned digits, never the display.
import { phoneCountry } from "../constants/phoneCountries";

// A normal no-break space: wide enough to read as a gap, and it never lets a
// number wrap across two lines.
export const NBSP = " ";

export const groupThousands = (digits: string): string =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);

export const displayAmount = (amount: string): string => {
  const [whole, ...fraction] = amount.split(".");

  return fraction.length === 0
    ? groupThousands(whole)
    : `${groupThousands(whole)}.${fraction.join("")}`;
};

type AmountInputOptions = { decimals?: number };

// Leading zeros are dropped so "0120" cannot be shown as "0 120"; a lone zero
// stays so the field never silently empties under the payer's cursor.
export const cleanAmountInput = (raw: string, { decimals = 0 }: AmountInputOptions = {}): string => {
  const cleaned = raw.replace(decimals > 0 ? /[^0-9.]/g : /[^0-9]/g, "");
  const [whole, ...rest] = cleaned.split(".");
  const integer = whole.replace(/^0+(?=\d)/, "");

  if (rest.length === 0) return integer;

  return `${integer}.${rest.join("").slice(0, decimals)}`;
};

export const cleanPhoneInput = (raw: string, countryCode: string): string => {
  const country = phoneCountry(countryCode);
  const digits = raw.replace(/\D/g, "");

  if (!country) return digits;

  const dialDigits = country.dialCode.replace(/\D/g, "");
  // Only a number longer than a national one can carry the dialling code, so
  // a national number that happens to start with those digits is kept. The
  // code may be written 00237 as well as +237.
  const prefix = [`00${dialDigits}`, dialDigits].find(
    code => digits.length > country.nationalLength && digits.startsWith(code)
  );
  const national = prefix ? digits.slice(prefix.length) : digits;

  return national.slice(0, country.nationalLength);
};

export const displayPhone = (national: string, countryCode: string): string => {
  const groups = phoneCountry(countryCode)?.groups;

  if (!groups) return national;

  const parts: string[] = [];
  let start = 0;
  for (const size of groups) {
    const part = national.slice(start, start + size);
    if (part) parts.push(part);
    start += size;
  }
  const remainder = national.slice(start);
  if (remainder) parts.push(remainder);

  return parts.join(NBSP);
};

const isSignificant = (char: string) => /[0-9.]/.test(char);

// How many digits (and decimal points) precede the caret: the one thing about
// its position that survives reformatting.
export const significantBefore = (value: string, caret: number): number =>
  Array.from(value.slice(0, caret)).filter(isSignificant).length;

export const caretAfterDigits = (masked: string, count: number): number => {
  let seen = 0;
  let position = 0;
  while (seen < count && position < masked.length) {
    if (isSignificant(masked[position])) seen += 1;
    position += 1;
  }

  return position;
};
